import React, { type ReactNode, useCallback, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { message } from 'antd';
import classNames from 'classnames';

import { ENTITY_TYPES } from '@/shared/api/openmetadata';
import { removeFqnQuotes } from '@/shared/lib/format';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import { buildServiceUrl } from '@/shared/lib/url/serviceUrl';
import type { LineageNodeData } from '@/shared/types/lineageNode';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { ServiceIcon } from '@/shared/ui/ServiceIcon';

import styles from './LineageNodeDrawer.module.css';

export type InfoRow = {
  label: string;
  value: ReactNode;
  linkTo?: string;
};

function isPlainInfoValue(value: InfoRow['value']): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

export type DrawerSection = {
  id: string;
  title: string;
  content: ReactNode;
  collapsible?: boolean;
};

type LineageNodeDrawerProps = {
  open: boolean;
  onClose: () => void;
  data: LineageNodeData;
  infoRows: InfoRow[];
  sections: DrawerSection[];
};

export const LineageNodeDrawer: React.FC<LineageNodeDrawerProps> = ({ open, onClose, data, infoRows, sections }) => {
  const navigate = useNavigate();

  const navigationUrl = useMemo((): string | null => {
    if (!data) return null;

    const { entityType, fullyQualifiedName } = data;

    if (entityType === ENTITY_TYPES.DOMAIN) {
      return `/domain/${encodeURIComponent(fullyQualifiedName)}/sub-domains`;
    }

    if (entityType === ENTITY_TYPES.DATA_PRODUCT) {
      return `/dataProduct/${encodeURIComponent(removeFqnQuotes(fullyQualifiedName))}`;
    }

    const serviceUrl = buildServiceUrl({
      serviceName: fullyQualifiedName,
      entityType,
    });

    if (serviceUrl) {
      return serviceUrl;
    }

    return buildAssetDetailUrl(entityType, fullyQualifiedName);
  }, [data]);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!data || !navigator.clipboard) {
        message.error('클립보드가 지원되지 않습니다');
        return;
      }
      try {
        await navigator.clipboard.writeText(data.fullyQualifiedName);
        message.success('복사되었습니다');
      } catch (error) {
        console.error('Copy failed:', error);
        message.error('복사에 실패했습니다');
      }
    },
    [data]
  );

  const handleTitleClick = useCallback(() => {
    if (navigationUrl) {
      navigate(navigationUrl);
    }
  }, [navigationUrl, navigate]);

  if (!data) {
    return null;
  }

  const titleText = data.displayName || data.label;

  return (
    <div className={classNames(styles.drawerContainer, { [styles.open]: open })}>
      <div className={styles.drawerHeader}>
        <div className={styles.drawerTitle}>
          <div className={styles.titleContent}>
            <div className={styles.hierarchyRow}>
              {data.hierarchyPath && (
                <div className={styles.hierarchyPath}>
                  <EllipsisText text={data.hierarchyPath} tooltipPlacement="topLeft" />
                </div>
              )}
              <button
                onClick={onClose}
                className={classNames(styles.closeButton, styles.button)}
                aria-label="드로어 닫기"
                type="button">
                <CloseOutlined />
              </button>
            </div>
            <div className={styles.titleRow}>
              {data.entityType === ENTITY_TYPES.DOMAIN ? (
                <Icon name="globe" size={20} color="var(--opds-glo-palette-gray-30)" />
              ) : (
                <ServiceIcon serviceType={data.serviceType} size={20} />
              )}
              <div className={styles.titleMain}>
                <EllipsisText
                  text={titleText}
                  className={classNames(styles.titleEllipsis, { [styles.titleClickable]: navigationUrl !== null })}
                  tooltipPlacement="topLeft">
                  <span
                    onClick={navigationUrl ? handleTitleClick : undefined}
                    role={navigationUrl ? 'button' : undefined}
                    tabIndex={navigationUrl ? 0 : undefined}
                    onKeyDown={
                      navigationUrl
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleTitleClick();
                            }
                          }
                        : undefined
                    }>
                    {titleText}
                  </span>
                </EllipsisText>
                <button
                  onClick={handleCopy}
                  className={classNames(styles.copyButton, styles.button)}
                  aria-label="FQN 복사"
                  type="button">
                  <CopyOutlined />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.drawerContent}>
        {infoRows.length > 0 && (
          <div className={styles.infoSection}>
            {infoRows.map((row) => (
              <div key={row.label} className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  <EllipsisText text={row.label} tooltipPlacement="topLeft" />
                </span>
                <span className={styles.infoValue}>
                  {isPlainInfoValue(row.value) ? (
                    <EllipsisText text={String(row.value)} tooltipPlacement="topRight" />
                  ) : (
                    row.value
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {sections.map((section) => (
          <div key={section.id} className={styles.section}>
            <h3 className={styles.sectionTitle}>{section.title}</h3>
            {section.content}
          </div>
        ))}
      </div>
    </div>
  );
};
